import { type Cell, flexRender } from "@tanstack/react-table";
import { type RefObject, useEffect, useState } from "react";

import { type BaseObject } from "../../../../evidence/base";
import { cn } from "../../../../utils/cn";
import { TableCell } from "../../../atoms/table/table-cell";
import { useDataTableContext } from "../../context";
import { DataTableBodyCell } from "./data-table-body-cell";

/**
 * Scrollable element (`<div>`) is a direct parent of the `<table>` element.
 */
function getTableScrollContainer(
  tableBodyRef: RefObject<HTMLTableSectionElement | null>,
) {
  return tableBodyRef.current?.closest("table")?.parentElement ?? null;
}

const ACTIONS_INLINE_VISIBILITY_OFFSET = 96;

export function useShouldRenderActionsOnHover() {
  const [shouldRenderActionsOnHover, setShouldRenderActionsOnHover] =
    useState(false);

  const { tableBodyRef, ...table } = useDataTableContext();

  const visibleLeafColumns = table.getVisibleLeafColumns();
  const totalColumnsWidth = visibleLeafColumns.reduce(
    (acc, column) => acc + column.getSize(),
    0,
  );

  /**
   * Setup scroll observer to update actions rendering - inline or on hover.
   */
  useEffect(() => {
    const tableWrapper = getTableScrollContainer(tableBodyRef);

    if (!tableWrapper) {
      throw new Error(
        "[useShouldRenderActionsOnHover]: Scrollable table wrapper not found",
      );
    }

    /**
     * Update rendering actions inline or on hover.
     * If table is horizontally scrollable and is not scrolled to the
     * right edge (actions column is not visible), we render actions on hover.
     */
    const updateActionsRendering = () => {
      const hasHorizontalOverflow =
        totalColumnsWidth > tableWrapper.clientWidth;

      const rightEdgeScrollLeft = Math.max(
        totalColumnsWidth - tableWrapper.clientWidth,
        0,
      );
      const isScrolledToRightEdge =
        tableWrapper.scrollLeft >=
        rightEdgeScrollLeft - ACTIONS_INLINE_VISIBILITY_OFFSET;

      setShouldRenderActionsOnHover(
        hasHorizontalOverflow && !isScrolledToRightEdge,
      );
    };

    updateActionsRendering();

    const resizeObserver = new ResizeObserver(() => {
      updateActionsRendering();
    });

    resizeObserver.observe(tableWrapper);
    tableWrapper.addEventListener("scroll", updateActionsRendering, {
      passive: true,
    });

    return () => {
      tableWrapper.removeEventListener("scroll", updateActionsRendering);
      resizeObserver.disconnect();
    };
  }, [tableBodyRef, totalColumnsWidth]);

  return shouldRenderActionsOnHover;
}

export function DataTableActionsCell<TData extends BaseObject>({
  cell,
  hasContextMenuActions,
}: {
  cell: Cell<TData, any>;
  hasContextMenuActions: boolean;
}) {
  const shouldRenderActionsOnHover = useShouldRenderActionsOnHover();

  if (shouldRenderActionsOnHover) {
    return (
      <TableCell
        className={cn(
          "sticky ml-auto hidden h-auto items-center justify-center bg-inherit px-4 py-0 group-hover:flex",
          hasContextMenuActions ? "right-12" : "right-0",
        )}
      >
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </TableCell>
    );
  }

  return <DataTableBodyCell cell={cell} />;
}
